/**
 * AdminResetPassword — ADR-0022 admin scope.
 *
 * Konsumuje token resetu hasla admina. URL z maila:
 *   `https://admin.<base>/reset-password/:token`
 * Token czytany z params (useParams), POST body: { token, password }.
 *
 * Stany:
 *  - 'idle' — formularz aktywny.
 *  - 'submitting' — request w locie.
 *  - 'expired' — backend zwrocil 410 INVALID_OR_EXPIRED → ekran „Link wygasl".
 *
 * Walidacje frontowe (best-effort): zgodnosc password=confirm. Backend wymusza
 * pelna policy i zwraca 422 PASSWORD_POLICY_VIOLATION z details[].
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

type State = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'expired' };

interface BackendError {
  error?: string;
  details?: string[] | Record<string, string[]>;
}

function extractError(err: unknown): { status: number | null; data: BackendError | null } {
  const e = err as { response?: { status?: number; data?: BackendError } };
  return {
    status: e.response?.status ?? null,
    data: e.response?.data ?? null,
  };
}

function policyErrorsToMessages(details: string[] | undefined, t: (k: string) => string): string[] {
  if (!details) return [];
  return details.map((code) => {
    switch (code) {
      case 'TOO_SHORT':
        return t('auth.adminReset.policy.tooShort');
      case 'TOO_LONG':
        return t('auth.adminReset.policy.tooLong');
      case 'NO_DIGIT':
        return t('auth.adminReset.policy.noDigit');
      case 'NO_UPPER':
        return t('auth.adminReset.policy.noUpper');
      case 'NO_LOWER':
        return t('auth.adminReset.policy.noLower');
      case 'NO_SPECIAL':
        return t('auth.adminReset.policy.noSpecial');
      default:
        return code;
    }
  });
}

export function AdminResetPassword() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { admin, loading } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [policyErrors, setPolicyErrors] = useState<string[]>([]);
  const [mismatchError, setMismatchError] = useState(false);

  useEffect(() => {
    if (!loading && admin !== null) {
      navigate('/', { replace: true });
    }
  }, [loading, admin, navigate]);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'expired' });
    }
  }, [token]);

  if (loading || admin !== null) return null;

  const submitting = state.kind === 'submitting';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPolicyErrors([]);
    setMismatchError(false);

    if (password !== confirm) {
      setMismatchError(true);
      return;
    }

    if (!token) {
      setState({ kind: 'expired' });
      return;
    }

    setState({ kind: 'submitting' });

    try {
      await api.post('/admin/reset-password', { token, password });
      toast.show({
        variant: 'success',
        title: t('auth.adminReset.successTitle'),
        description: t('auth.adminReset.successDescription'),
      });
      navigate('/login', { replace: true });
    } catch (err) {
      const { status, data } = extractError(err);
      if (status === 410) {
        setState({ kind: 'expired' });
        return;
      }
      if (status === 422 && data?.error === 'PASSWORD_POLICY_VIOLATION') {
        const details = Array.isArray(data.details) ? data.details : [];
        setPolicyErrors(policyErrorsToMessages(details, t));
        setState({ kind: 'idle' });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('auth.adminReset.errors.genericTitle'),
        description: t('auth.adminReset.errors.genericDescription'),
      });
      setState({ kind: 'idle' });
    }
  };

  if (state.kind === 'expired') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
        <div
          className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl"
          data-testid="admin-reset-password-expired"
        >
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-text-primary">
              {t('auth.adminReset.expiredTitle')}
            </h1>
            <p className="mt-3 text-sm text-text-tertiary">
              {t('auth.adminReset.expiredDescription')}
            </p>
          </div>
          <div className="mt-8 flex flex-col gap-3">
            <Link to="/forgot-password">
              <Button className="w-full">{t('auth.adminReset.requestNewLink')}</Button>
            </Link>
            <Link to="/login">
              <Button variant="secondary" className="w-full">
                {t('auth.adminReset.backToLogin')}
              </Button>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-text-primary">{t('auth.adminReset.title')}</h1>
          <p className="mt-2 text-sm text-text-tertiary">{t('auth.adminReset.subtitle')}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Input
            label={t('auth.adminReset.passwordLabel')}
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            data-testid="admin-reset-password-new"
          />
          <Input
            label={t('auth.adminReset.confirmLabel')}
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
            data-testid="admin-reset-password-confirm"
          />

          <p className="text-xs text-text-tertiary">{t('auth.adminReset.policyHint')}</p>

          {mismatchError && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              data-testid="admin-reset-password-mismatch"
            >
              {t('auth.adminReset.errors.mismatch')}
            </div>
          )}

          {policyErrors.length > 0 && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              data-testid="admin-reset-password-policy-errors"
            >
              <ul className="list-inside list-disc space-y-1">
                {policyErrors.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            loading={submitting}
            data-testid="admin-reset-password-submit"
          >
            {submitting ? t('auth.adminReset.submitting') : t('auth.adminReset.submit')}
          </Button>
        </form>
      </div>
    </main>
  );
}
